import React from "react";

/**
 * EmptyState — unified empty state component.
 *
 * Default (no props): full card with dashed border — use for "list has no items at all".
 * compact=true: minimal inline version — use for "search returned no results".
 */
export default function EmptyState({
  icon,
  title,
  description,
  action,
  // Optional shorthand for a standard CTA button — avoids inline JSX in callers.
  // If both `action` and `actionLabel` are provided, `action` takes precedence.
  actionLabel,
  actionHref,
  actionOnClick,
  compact = false,
  className = "",
}) {
  const resolvedAction = action || (actionLabel && (
    actionHref
      ? <a href={actionHref} className="inline-flex items-center gap-2 bg-[#1b4f9c] text-white rounded-[20px] px-6 py-3 font-semibold text-[15px] shadow-[0_4px_12px_rgba(27,79,156,0.2)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80">{actionLabel}</a>
      : <button type="button" onClick={actionOnClick} className="inline-flex items-center gap-2 bg-[#1b4f9c] text-white rounded-[20px] px-6 py-3 font-semibold text-[15px] shadow-[0_4px_12px_rgba(27,79,156,0.2)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80">{actionLabel}</button>
  ));

  // Compact variant — minimal, no card/border, used inside search result containers
  if (compact) {
    return (
      <div className={`animate-fade flex flex-col items-center py-10 px-6 text-center ${className}`}>
        {icon && (
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-[14px] bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500">
            {icon}
          </div>
        )}
        {title && (
          <p className="text-[15px] font-medium text-slate-500 dark:text-slate-400 leading-snug">
            {title}
          </p>
        )}
        {description && (
          <p className="mt-1 text-[13px] text-slate-400 dark:text-slate-500 leading-relaxed">
            {description}
          </p>
        )}
        {resolvedAction && <div className="mt-4">{resolvedAction}</div>}
      </div>
    );
  }

  // Default variant — full card with dashed border
  return (
    <div
      className={`
        animate-fade
        rounded-card
        border border-dashed border-slate-200 dark:border-slate-700/50
        bg-slate-50/20 dark:bg-slate-800/20
        px-6 py-10
        text-center
        ${className}
      `}
    >
      <div className="flex flex-col items-center">

        {icon && (
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-[20px] bg-slate-100/80 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500">
            {icon}
          </div>
        )}

        {title && (
          <h3 className="text-[15px] font-semibold text-slate-700 dark:text-slate-200">
            {title}
          </h3>
        )}

        {description && (
          <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-slate-400 dark:text-slate-500">
            {description}
          </p>
        )}

        {resolvedAction && (
          <div className="mt-5">
            {resolvedAction}
          </div>
        )}
      </div>
    </div>
  );
}
